import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Leaf, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ name: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("The passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/accept-invite", {
        token,
        name: form.name.trim(),
        password: form.password,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "This invitation could not be used."));
    }
    setLoading(false);
  };

  return (
    <main data-testid="accept-invite-page" className="flex min-h-screen items-center justify-center bg-ivory pt-[76px]">
      <div className="w-full max-w-md px-5 py-16">
        <div className="rounded-[2.5rem] border border-forest/10 bg-white/75 p-9 shadow-[0_25px_60px_rgba(23,63,53,0.12)] sm:p-11">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sage">
            {done ? <Check className="h-5 w-5 text-forest" /> : <ShieldCheck className="h-5 w-5 text-forest" />}
          </div>
          {done ? (
            <div data-testid="accept-invite-success">
              <h1 className="mt-6 font-serif text-3xl text-forest">You're all set</h1>
              <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                Your Admin Center access is active. Sign in with your email and new password.
                As a privileged account, each sign-in also emails you a short security code.
              </p>
              <button
                data-testid="accept-invite-login"
                onClick={() => navigate("/login", { state: { from: "/admin" } })}
                className="mt-7 inline-flex w-full items-center justify-center rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-colors hover:bg-forest-deep"
              >
                Continue to Sign In
              </button>
            </div>
          ) : (
            <>
              <h1 className="mt-6 font-serif text-3xl text-forest">Accept your invitation</h1>
              <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                Set your name and a secure password to activate your Admin Center access.
              </p>
              <form data-testid="accept-invite-form" onSubmit={submit} className="mt-7 flex flex-col gap-4">
                <input
                  data-testid="invite-name"
                  required
                  placeholder="Your name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3.5 text-sm outline-none focus:border-gold"
                />
                <input
                  data-testid="invite-password"
                  required
                  type="password"
                  minLength={8}
                  placeholder="Choose a password (8+ characters)"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className="w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3.5 text-sm outline-none focus:border-gold"
                />
                <input
                  data-testid="invite-confirm"
                  required
                  type="password"
                  minLength={8}
                  placeholder="Repeat the password"
                  value={form.confirm}
                  onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                  className="w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3.5 text-sm outline-none focus:border-gold"
                />
                <button
                  data-testid="invite-submit"
                  disabled={loading || !token}
                  className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-colors hover:bg-forest-deep disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Leaf className="h-4 w-4" />}
                  Activate My Access
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
